import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { OVERVIEW_CONFIGS } from "../constants/overviewConfigs";
import ModalHeader from "../shared/ModalHeader";
import AddItemBtn from "../shared/AddItemBtn";

const dialogSx = {
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    background: "#1a1a1a",
    color: "text.primary",
    maxHeight: "calc(100vh - 64px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    m: 2,
  },
};

const scrollbarSx = {
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.05)" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.2)",
    borderRadius: "4px",
  },
};

/**
 * OverviewModal
 *
 * Props:
 *  overviewSection   {string|null}
 *  form              {object}
 *  setForm           {fn}
 *  effectiveId       {string|null}
 *  saveToBackend     {fn}
 *  onClose           {fn}
 *  onCardClick       {fn(index)}
 *  onAddClick        {fn}
 */
const OverviewModal = ({
  overviewSection,
  form,
  setForm,
  effectiveId,
  saveToBackend,
  onClose,
  onCardClick,
  onAddClick,
}) => {
  if (!overviewSection) return null;

  const config = OVERVIEW_CONFIGS[overviewSection];
  const data = form[overviewSection];
  const itemsKey = overviewSection === "pricing" ? "packages" : config.itemsKey;
  const items = data[itemsKey];

  const updateExtraFields = (updated) => {
    const newForm = { ...form, [overviewSection]: updated };
    setForm(newForm);
    setTimeout(() => {
      if (effectiveId) saveToBackend(newForm);
    }, 1000);
  };

  const removeItem = (i) => {
    const next = items.filter((_, idx) => idx !== i);
    const newForm = {
      ...form,
      [overviewSection]: {
        ...form[overviewSection],
        [itemsKey]: next,
      },
    };
    setForm(newForm);
    setTimeout(() => {
      if (effectiveId) saveToBackend(newForm);
    }, 1000);
  };

  return (
    <Dialog
      open={!!overviewSection}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      sx={dialogSx}
    >
      <ModalHeader
        icon={config.icon}
        title={config.label}
        subtitle={config.countLabel(items)}
        onClose={onClose}
      />

      <DialogContent
        sx={{ p: 3, pt: "20px", overflowY: "auto", flex: 1, minHeight: 0, ...scrollbarSx }}
      >
        {config.extraFields(data, updateExtraFields)}

        <Divider sx={{ mb: 2, mt: 2 }} />

        {items.length === 0 ? (
          <Box textAlign="center" py={5}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              No {config.label.toLowerCase()} yet
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddClick}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "#3b82f6",
                color: "#3b82f6",
                "&:hover": {
                  borderColor: "#2563eb",
                  color: "#2563eb",
                  background: "rgba(59,130,246,0.05)",
                },
              }}
            >
              Add {config.itemLabel}
            </Button>
          </Box>
        ) : (
          // renderSummary now receives onDelete directly —
          // SummaryCard renders the delete button internally on hover
          <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
            {items.map((item, i) =>
              config.renderSummary(
                item,
                () => onCardClick(i),   // onClick → open detail
                () => removeItem(i),    // onDelete → remove from list
              )
            )}
          </Box>
        )}

        <AddItemBtn label={`Add ${config.itemLabel}`} onClick={onAddClick} />
      </DialogContent>
    </Dialog>
  );
};

export default OverviewModal;